import React, { useState, useRef, useEffect, useContext } from 'react';
import Button, { IFuzionButtonProps } from '../Button';
import { IImage } from 'tbk-components/src/components/Image';
import TriangleImage from '../TriangleImage';
import Form from '../Form';
import Modal from 'tbk-components/src/components/Modal';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import { IDealerProps } from '../Dealer';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import { twMerge } from 'tailwind-merge';

declare const grecaptcha: any;

interface IGetAQuoteI18nProps {
	close?: string;
}

export interface IGetAQuoteProps extends IBaseElementProps {
	modalTitle?: string;
	modalSubTitle?: string;
	modalHeaderImage?: IImage;
	toggleButton?: IFuzionButtonProps;
	formId?: number;
	children?: any;
	productId?: number;
	flooringType?: string;
	productColor?: string;
	i18n?: IGetAQuoteI18nProps;
	context?: any;
	triggerButtonId?: string;
	triggerButton?: boolean;
	usRedirectMessage?: string;
	caRedirectMessage?: string;
}

const genAddressStr = (streetAddress, city, province, postalCode) => {
	const addressComponents = [streetAddress, city, province].filter(Boolean);
	const addressString = addressComponents.join(', ');
	const p = postalCode ? ` ${postalCode}` : '';

	return addressString + p;
};

const checkPostalZipType = (value) => {
	const usZipRegex = /^[0-9]{5}(?:-[0-9]{4})?$/; // Matches 5-digit or ZIP+4 format

	//This only supports US or CA, so we assume it's Canadian by default and if they are inputing any other format we ignore it and just go as normal.
	if (usZipRegex.test(value)) {
		return 'US';
	}

	return 'CA';
};

export const getAQuoteModalButtonClick = () => {
	const btn = document.querySelector(
		'#floating-cta-get-a-quote',
	) as HTMLButtonElement;

	if (btn) {
		btn.click();
	}
};

const GetAQuote: React.FC<IGetAQuoteProps> = withAppContext(
	({
		id,
		className,
		classNames = [],
		modalTitle,
		modalSubTitle,
		modalHeaderImage,
		toggleButton,
		formId,
		children,
		productId,
		flooringType,
		productColor,
		i18n,
		context,
		triggerButtonId,
		triggerButton = false,
		usRedirectMessage = 'It seems you have entered an American ZIP code on our Canadian site. Please checkout our USA version of the site!',
		caRedirectMessage = 'It seems you have entered a Canadian postal code on our American site. Please checkout our Canadian version of the site!',
	}) => {
		const lang = document.documentElement.lang.toLowerCase();
		const isCanada = lang.includes('-ca');
		const distanceUnit = isCanada ? 'km' : 'miles';

		// @ts-ignore
		const { assets: contextAssets, Api } = useContext(context);

		const [showModal, setShowModal] = useState(false);
		const [dealers, setDealers] = useState<IDealerProps[]>([]);
		const [isLoaded, setIsLoaded] = useState(false);
		const [productInfo, setProductInfo] = useState(null);
		const [formRendered, setFormRendered] = useState(false);
		const [prevClicked, setPrevClicked] = useState(false);

		const formRef = useRef(null);

		const getAQuoteModal = (
			<Modal
				onCancel={() => {
					setShowModal(false);
				}}
				modalClassNames={[
					'lg:max-w-[970px] lg:flex lg:items-center lg:justify-center lg:h-full',
				]}
				headerClassNames={[]}
				bodyClassNames={[]}
				showClose={false}
				showCancel={false}
				showSubmit={false}
				trapFocus
			>
				<div className="get-a-quote__container mx-auto mt-8 w-full max-w-[calc(100%-2rem)] overflow-hidden rounded-md bg-white lg:mt-0 lg:max-w-full">
					<div className="get-a-quote__header bg-gradient-rainbow-radial relative p-4 pt-8 lg:px-8 lg:py-8">
						<div className="lg:max-w-[700px]">
							{modalTitle && (
								<h2 className="mb-0">{modalTitle}</h2>
							)}
							{modalSubTitle && (
								<p className="lead mb-0 lg:mt-3">
									{modalSubTitle}
								</p>
							)}
						</div>
						<button
							type="button"
							aria-label={
								i18n?.close || 'Close get a quote modal'
							}
							className="absolute right-0 top-0 z-10 flex h-11 w-11 items-center justify-center lg:right-2 lg:top-2"
							onClick={() => {
								setShowModal(false);
							}}
						>
							<i className="icon-close text-2xl lg:text-3xl" />
						</button>
						{modalHeaderImage && (
							<TriangleImage
								classNames={[
									'hidden lg:block absolute bottom-0 -right-12',
								]}
								image={modalHeaderImage}
								mask="Get A Quote Modal"
							/>
						)}
					</div>
					<div
						className="get-a-quote__body p-4 lg:px-8 lg:py-10 lg:pt-0"
						ref={formRef}
					>
						{formId && children && (
							<Form theme="light">{children}</Form>
						)}
					</div>
				</div>
			</Modal>
		);

		const getDealers = async (postal_code) => {
			if (checkPostalZipType(postal_code) === 'CA') {
				postal_code =
					postal_code.slice(0, 3) + ' ' + postal_code.slice(3);
			}

			if (!isLoaded && !window.google.maps.Geocoder) return;

			const service = await new window.google.maps.Geocoder();

			const suffix = isCanada ? ' Canada' : '';

			service.geocode(
				{ address: postal_code + suffix },
				(results, status) => {
					if (status === 'OK') {
						const { lat, lng } = results[0].geometry.location;
						// convert miles to km
						const radius = isCanada ? 100 : 100 / 0.621371;
						const args = {
							lat: lat(),
							lng: lng(),
							radius: radius * 1000,
							num_results: 3,
							ignore_radius: true,
							country: isCanada ? 'CA' : 'US',
						};

						if (Api.getDealerResults) {
							Api.getDealerResults(args).then((response) => {
								if (response && Array.isArray(response)) {
									const resData = response.map(
										(item: any) => {
											return {
												...item,
												name: item.name.toLowerCase(),
												position: {
													lat: Number(item.lat),
													lng: Number(item.long),
												},
												distance:
													(isCanada
														? Math.round(
																item.distance /
																	1000,
															)
														: Math.round(
																item.distance /
																	0.621371 /
																	1000,
															)) +
													` ${distanceUnit}`,
												streetAddress:
													item.street_address.toLowerCase(),
												city: item.city.toLowerCase(),
												province: item.state,
												postalCode: item.postal,
											};
										},
									);
									setDealers(resData);
								}
							});
						}
					}
				},
			);
		};

		useEffect(() => {
			if (!showModal || !formRef.current) return;

			const websiteRegion = () => {
				let region = 'CA';
				const currentLang = document.documentElement.lang.toLowerCase();

				if (currentLang.includes('-us')) {
					region = 'US';
				}

				return region;
			};

			const handleBtnClick = (e) => {
				setFormRendered(false);
				setPrevClicked(false);

				//Handle submitting to the next page
				if (
					((e.target.closest('.user-postal-code') &&
						e.key === 'Enter') ||
						e.target.className ===
							'gform_next_button gform-theme-button button' ||
						e.target.className === 'gform_button button') &&
					e.target.closest('.modal.fade.show')
				) {
					// `user-postal-code` class name is defined in Gravity Form settings
					const postalCodeInput = e.target
						.closest('form')
						.querySelector(
							'.user-postal-code input',
						) as HTMLInputElement;

					if (postalCodeInput.value)
						getDealers(postalCodeInput.value);
				}

				//Handle validating the Postal ZIP Code
				if (e.type === 'click') {
					let postalNode;
					if (e.target.closest('.user-postal-code')) {
						postalNode = e.target;
						postalNode.addEventListener(
							'focusout',
							maybeShowRedirectOption,
							false,
						);
					} else {
						if (postalNode) {
							postalNode.removeEventListener(
								'focusout',
								maybeShowRedirectOption,
								false,
							);
						}
					}
				}
			};

			const handlePrevBtnClick = (e) => {
				if (
					e.target.className ===
					'gform_previous_button gform-theme-button gform-theme-button--secondary button'
				) {
					setPrevClicked(true);
				}
			};

			const maybeShowRedirectOption = (e) => {
				const val = e.target.value;
				const nextPageBtn = document.querySelector(
					'.gform_page_footer .gform_next_button',
				);
				if (val) {
					const inputRegion = checkPostalZipType(val);
					if (inputRegion !== websiteRegion()) {
						//This means the user's input is not matching the site's region. I.E, they are in the US website and input a Canadian postal code. Or vice versa.
						//We technically don't need to know which pattern it is, but all we need to do is recommend going to the other site.

						let message = usRedirectMessage;

						if (inputRegion === 'CA') {
							message = caRedirectMessage;
						}

						if (
							!e.target
								.closest('.user-postal-code')
								.querySelector('.redirect-suggestion')
						) {
							e.target
								.closest('.user-postal-code')
								.insertAdjacentHTML(
									'beforeend',
									`<p class="redirect-suggestion text-error px-3 py-1 text-sm [&_a]:text-sm [&_a]:text-error">${message}</p>`,
								);

							nextPageBtn.setAttribute('disabled', 'disabled');
						}
					} else {
						document.querySelector('.redirect-suggestion') &&
							document
								.querySelector('.redirect-suggestion')
								.remove();

						nextPageBtn.removeAttribute('disabled');
					}
				}
			};

			document.body.addEventListener('click', handleBtnClick);
			document.body.addEventListener('click', handlePrevBtnClick);
			window.addEventListener('keyup', handleBtnClick);
			return () => {
				document.body.removeEventListener('click', handleBtnClick);
				document.body.removeEventListener('click', handlePrevBtnClick);
				window.removeEventListener('keyup', handleBtnClick);
			};
		}, [showModal]);

		// Handle updating dealer fields after user makes a selection
		useEffect(() => {
			const handleDealerRadioSelection = (e: Event) => {
				if (!formRef.current) return;
				const target = e.target as HTMLInputElement;
				const isInForm = formRef.current.contains(target);
				const localDealers = target.closest('.local-dealers');

				if (!isInForm || !localDealers) return;

				if(!dealers || !Array.isArray(dealers) || dealers.length === 0) return;

				const dealerEmail = formRef.current.querySelector(
					'.dealer-email .ginput_container_email input[type="email"]',
				) as HTMLInputElement;

				const salesRepEmail = formRef.current.querySelector(
					'.sales-rep-email .ginput_container_email input[type="email"]',
				) as HTMLInputElement;

				const dealer = dealers.find(
					(d) => (d.dealer_contacts || []).find((contact: any) => contact.email === target.value),
				);

				const dealerEmailVal = dealer && Array.isArray(dealer.dealer_contacts) && dealer.dealer_contacts.length > 0 ? dealer.dealer_contacts[0].email : null;
				const salespersonEmailVal = dealer && Array.isArray(dealer.salespersons) && dealer.salespersons.length > 0 ? dealer.salespersons[0].email : null;

				if (dealer && dealerEmail && salesRepEmail) {
					dealerEmail.value = dealerEmailVal;
					salesRepEmail.value = salespersonEmailVal;
				}
			}

			document.body.addEventListener('change', handleDealerRadioSelection);

			return () => {
				document.body.removeEventListener('change', handleDealerRadioSelection);
			};
		}, [dealers, formRef]);

		useEffect(() => {
			if (!formRef.current) return;

			const localDealerRadios = formRef.current.querySelectorAll(
				'.local-dealers .ginput_container_radio .gfield_radio .gchoice',
			) as NodeListOf<HTMLDivElement>;

			if (localDealerRadios.length > 0 && dealers.length > 0) {
				for (let i = 0; i < localDealerRadios.length; i++) {
					const radio = localDealerRadios[i];
					const dealer = dealers[i];
					const radioLabel = radio.querySelector('label');
					const radioInput = radio.querySelector('input');

					if (dealer && formRendered) {
						const contacts = dealer?.dealer_contacts || [];
						radioInput.value = Array.isArray(contacts) ? contacts[0].email : null;
						radioLabel.innerHTML = `<span class="block font-header capitalize">${dealer.name}</span><span class="block text-sm leading-[1.6] text-medium">${dealer.distance}<span class="inline-block text-primary mx-[6px]">&middot;</span><span class="capitalize">${genAddressStr(dealer.streetAddress, dealer.city, dealer.province, dealer.postalCode)}</span></span>`;
					}
				}
			}
		}, [formRef, dealers, formRendered]);

		useEffect(() => {
			if (!showModal || !formRef.current) {
				return;
			}

			const formEl = document.querySelector('#get-quote-form');
			const id = formEl.getAttribute('data-form-id');

			if (!formEl) {
				return;
			}

			// Clone formEl inside form wrapper
			// @ts-ignore
			const formWrapper = formRef.current.querySelector('.form-wrapper');
			const gform = formEl.querySelector('.gform_wrapper');
			// @ts-ignore
			const clone = jQuery(gform).clone(true);
			// @ts-ignore
			jQuery(formWrapper).empty().append(gform);

			const fId = id;

			// We have to manually re-trigger the gform_post_render event because the form on product index page is cloned and appended to the modal
			// Check if the Gravity Forms object exists
			// @ts-ignore
			if (typeof gform !== 'undefined' && typeof jQuery !== 'undefined') {
				// Trigger the gform_post_render event manually
				// @ts-ignore
				jQuery(document).trigger('gform_post_render', [
					fId,
					// @ts-ignore
					jQuery(`#gform_wrapper_${fId}`),
				]);
			}

			// `product-id`, `flooring-type` and `product-color` class names are defined in Gravity Form settings
			const productIdInput = formRef.current.querySelector(
				'.product-id .ginput_container input[type="text"]',
			) as HTMLInputElement;
			const flooringTypeInput = formRef.current.querySelector(
				'.flooring-type .ginput_container input[type="text"]',
			) as HTMLInputElement;
			const productColorInput = formRef.current.querySelector(
				'.product-color .ginput_container input[type="text"]',
			) as HTMLInputElement;

			if (!productIdInput || !flooringTypeInput || !productColorInput)
				return;

			productIdInput.value = productInfo.productId;
			flooringTypeInput.value = productInfo.productColor;
			productColorInput.value = productInfo.productCollection;

			productIdInput.closest('.gfield').classList.add('input-filled');
			flooringTypeInput.closest('.gfield').classList.add('input-filled');
			productColorInput.closest('.gfield').classList.add('input-filled');

			return () => {
				// Restore form
				// @ts-ignore
				jQuery(formEl).append(clone);
			};
		}, [formRef, showModal, productInfo]);

		// On product index page, the reCaptcha widget id get lost during the prev/next step re-render
		// So we need to reset the reCaptcha widget id on the re-rendered form
		useEffect(() => {
			if (!showModal || !formRef.current) {
				return;
			}

			const targetNode = formRef.current;

			const config = {
				subtree: true,
				childList: true,
			};

			const callback = (mutationList, observer) => {
				for (const mutation of mutationList) {
					if (
						mutation.type === 'childList' &&
						mutation.addedNodes.length > 0
					) {
						mutation.addedNodes.forEach((node) => {
							if (
								node?.classList?.contains('request-local-pricing')
							) {
								// Delay to make sure the reCaptcha widget is rendered
								setTimeout(() => {
									const captcha =
										node.querySelector('.ginput_recaptcha');

									if (
										captcha &&
										!captcha.hasAttribute(
											'data-widget-id',
										) &&
										typeof grecaptcha !== 'undefined' &&
										grecaptcha.render &&
										prevClicked
									) {
										const widgetId = grecaptcha.render(
											captcha,
											{
												sitekey:
													captcha.getAttribute(
														'data-sitekey',
													),
											},
										);
										captcha.setAttribute(
											'data-widget-id',
											widgetId,
										);
									}
								}, 1000);
							}
						});
					}
				}
			};

			const observer = new MutationObserver(callback);

			observer.observe(targetNode, config);

			return () => {
				observer.disconnect();
			};
		}, [formRef, showModal, productInfo, formRendered, prevClicked]);

		useEffect(() => {
			if (!formRef.current || !showModal) {
				return;
			}

			// Hide the "Amount required" field if product is an accessoty
			const amountRequiredInput = formRef.current.querySelector(
				'.field-amount-required',
			) as HTMLSelectElement;
			const amountRequiredGField =
				amountRequiredInput &&
				(amountRequiredInput.closest('.gfield') as HTMLDivElement);
			if (
				productInfo &&
				['Accessories', 'Accessoire'].includes(
					productInfo.productColor,
				) &&
				amountRequiredGField
			) {
				amountRequiredGField.style.display = 'none';
			} else if (amountRequiredGField) {
				amountRequiredGField.style.display = '';
			}
		}, [formRef, showModal, productInfo]);

		useEffect(() => {
			if (typeof (window as any).jQuery !== 'undefined') {
				(window as any)
					.jQuery(document)
					.on(
						'gform_page_loaded',
						function (event, form_id, current_page) {
							if (current_page >= 2) {
								// Emit the gform_post_render event using global jQuery
								(window as any)
									.jQuery(document)
									.trigger('gform_post_render');
							}
						},
					);
			}

			// Sometimes the dealer data is returned and updated before the form is rendered
			// So we need to make sure the form is rendered before we can update the dealer radio inputs
			const setFormState = () => {
				setFormRendered(true);
			};

			// @ts-ignore
			jQuery(document).on('gform_post_render', setFormState);

			return () => {
				// @ts-ignore
				jQuery(document).off('gform_post_render', setFormState);
			};
		}, []);

		return (
			<Wrapper
				apiKey={
					// @ts-ignore
					window.GOOGLE_MAPS_API_KEY
				}
				callback={(status: Status) => {
					if (status === 'SUCCESS') {
						setIsLoaded(true);
					}
				}}
				// We're adding below the same options as the ones found in
				// the Map component to fix the crash that happens when someone
				// tries to use the search in the header.
				// In an ideal setting (full react app), we would have wrapped
				// the whole app in the Wrapper component and passed the API key.
				libraries={['marker']}
				language={document.documentElement.lang}
			>
				<div
					id={id}
					className={className || twMerge('pl-0', ...classNames)}
					// To prevent floating cta bar being toggled every time the modal element is clicked
					onClick={(e) => {
						e.stopPropagation();

						//stopPropagation makes it so we cant close the modal by clicking outside, so we need to manually look for that type of click.
						const target = e.target as HTMLElement;
						if (
							!target.closest('.get-a-quote__container') &&
							showModal
						) {
							setShowModal(false);
						}
					}}
				>
					{toggleButton?.link?.label && (
						<Button
							{...toggleButton}
							id={triggerButtonId}
							classNames={['btn-sm']}
							onClick={() => {
								if (!triggerButton) {
									if (
										productId &&
										flooringType &&
										productColor
									) {
										setProductInfo({
											productId: productId,
											productColor: flooringType,
											productCollection: productColor,
										});
									}
									setShowModal(true);
								} else {
									getAQuoteModalButtonClick();
								}
							}}
						/>
					)}

					{showModal && getAQuoteModal}
				</div>
			</Wrapper>
		);
	},
);

export default GetAQuote;
